import { useEffect, useState } from "react";
import client from "../lib/api";
import { Trophy, Truck, Lightning, ChatCircleDots } from "@phosphor-icons/react";

/**
 * Driver Thanks Leaderboard.
 *
 * Ranks drivers by median POD-upload lag (delivered_at → POD uploaded_at).
 * Faster is better — top performers get visible credit for keeping AR
 * clean. Also shows how many "thanks SMS" the driver has received from
 * dispatch, so ops sees at a glance who's earning the shout-outs.
 *
 * Auto-hides on fresh accounts with zero driver-uploaded PODs.
 */
function _formatLag(hours) {
  if (hours === null || hours === undefined) return "—";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

export default function DriverThanksLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/drivers/thanks-leaderboard?limit=5")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!data || !data.drivers?.length) return null;

  return (
    <div
      data-testid="driver-thanks-leaderboard"
      className="card-surface p-5 sm:p-6"
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center">
          <Trophy size={18} weight="fill" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
            Fastest PODs
          </div>
          <div className="font-display text-lg font-semibold text-white leading-tight">
            Driver leaderboard
          </div>
        </div>
        <div className="ml-auto text-[10px] text-neutral-500 tabular-nums">
          {data.total_pods} POD{data.total_pods === 1 ? "" : "s"} tracked
        </div>
      </div>

      <ol className="space-y-2" data-testid="driver-thanks-leaderboard-list">
        {data.drivers.map((row, i) => {
          const rank = i + 1;
          const isPodium = rank <= 3;
          const rankTone = rank === 1
            ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
            : rank === 2
            ? "bg-neutral-400/20 text-neutral-200 border border-neutral-400/40"
            : rank === 3
            ? "bg-orange-500/20 text-orange-300 border border-orange-500/40"
            : "bg-white/[0.03] text-neutral-400 border border-white/10";
          return (
            <li
              key={row.driver_id}
              data-testid={`driver-thanks-row-${row.driver_id}`}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                isPodium
                  ? "border-amber-500/15 bg-amber-500/[0.02]"
                  : "border-white/5 bg-white/[0.02]"
              }`}
            >
              <div
                className={`shrink-0 w-7 h-7 rounded-full text-xs font-bold font-mono flex items-center justify-center ${rankTone}`}
              >
                {rank}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{row.name}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  {row.truck_number && (
                    <span className="inline-flex items-center gap-1">
                      <Truck size={10} />
                      {row.truck_number}
                    </span>
                  )}
                  <span>
                    {row.pod_count} POD{row.pod_count === 1 ? "" : "s"}
                  </span>
                  {row.thanks_received > 0 && (
                    <span className="inline-flex items-center gap-1 text-emerald-300">
                      <ChatCircleDots size={10} weight="fill" />
                      {row.thanks_received} thanks
                    </span>
                  )}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div
                  data-testid={`driver-thanks-median-${row.driver_id}`}
                  className="text-sm font-semibold text-white tabular-nums inline-flex items-center gap-1"
                >
                  <Lightning size={11} weight="fill" className="text-amber-400" />
                  {_formatLag(row.median_lag_hours)}
                </div>
                <div className="text-[10px] text-neutral-600">
                  median lag
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
